// Firebase Cloud Messaging utilities
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { ref, set, get, remove } from 'firebase/database';
import { db, messaging } from '../firebase';

// VAPID key for web push (you'll need to generate this in Firebase Console)
// Go to: Firebase Console → Project Settings → Cloud Messaging → Web Push certificates
const VAPID_KEY = 'BHksUbmrbOIpee9_-gBC-gEz5inQaWOL04JbgqxbCOBK4mFz152FhS_xjngjZlVg52sYfFbHfqOiD-SkeUeCCF0'; // TODO: Generate in Firebase Console

/**
 * Request notification permission and get FCM token
 * @param {Object} user - Current authenticated user
 * @returns {Promise<string|null>} FCM token or null if permission denied
 */
export const requestNotificationPermission = async (user) => {
  if (!user) {
    console.log('No user provided');
    return null;
  }

  // Check if messaging is available (won't be on localhost)
  if (!messaging) {
    console.log('Firebase Messaging not available (requires HTTPS)');
    return null;
  }

  // Check if notifications are supported
  if (!('Notification' in window)) {
    console.log('Notifications not supported in this browser');
    return null;
  }

  // Check if service workers are supported
  if (!('serviceWorker' in navigator)) {
    console.log('Service Workers not supported in this browser');
    return null;
  }

  try {
    // Request permission
    const permission = await Notification.requestPermission();
    
    if (permission !== 'granted') {
      console.log('Notification permission denied');
      return null;
    }

    console.log('✅ Notification permission granted');

    // Register service worker
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    console.log('✅ Service worker registered');

    // Get FCM token
    const token = await getToken(messaging, { 
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration
    });

    if (!token) {
      console.log('No FCM token received');
      return null;
    }

    console.log('✅ FCM token received:', token.substring(0, 20) + '...');

    // Save token to Firebase with device info
    const tokenId = btoa(token).substring(0, 20); // Create short ID from token
    const tokenRef = ref(db, `users/${user.uid}/fcmTokens/${tokenId}`);
    
    await set(tokenRef, {
      token,
      device: getDeviceInfo(),
      createdAt: Date.now(),
      lastSeen: Date.now()
    });

    console.log('✅ FCM token saved to Firebase');

    return token;
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return null;
  }
};

/**
 * Check if user has granted notification permission
 * @returns {boolean}
 */
export const hasNotificationPermission = () => {
  if (!('Notification' in window)) return false;
  return Notification.permission === 'granted';
};

/**
 * Listen for foreground messages (when app is open)
 * @param {Function} callback - Function to call when message received
 * @returns {Function} Unsubscribe function
 */
export const onForegroundMessage = (callback) => {
  if (!('Notification' in window)) return () => {};
  if (!messaging) return () => {}; // Skip if messaging not available
  
  try {
    return onMessage(messaging, (payload) => {
      console.log('📬 Foreground message received:', payload);
      callback(payload);
      
      // Show browser notification for foreground messages
      if (hasNotificationPermission() && payload.notification) {
        new Notification(payload.notification.title, {
          body: payload.notification.body,
          icon: '/logo192.png',
          tag: payload.data?.matchId || 'default',
          data: payload.data
        });
      }
    });
  } catch (error) {
    console.error('Error setting up foreground message listener:', error);
    return () => {};
  }
};

/**
 * Delete FCM token (when user disables notifications)
 * @param {Object} user - Current authenticated user
 */
export const deleteNotificationToken = async (user) => {
  if (!user) return;

  try {
    // Get all tokens
    const tokensRef = ref(db, `users/${user.uid}/fcmTokens`);
    const snapshot = await get(tokensRef);
    
    if (!snapshot.exists()) return;

    // Delete all tokens
    await remove(tokensRef);
    console.log('✅ FCM tokens deleted');
  } catch (error) {
    console.error('Error deleting FCM tokens:', error);
  }
};

/**
 * Get device info for token identification
 * @returns {string}
 */
function getDeviceInfo() {
  const ua = navigator.userAgent;
  let device = 'Unknown';
  
  if (/iPhone|iPad|iPod/.test(ua)) {
    device = 'iOS';
  } else if (/Android/.test(ua)) {
    device = 'Android';
  } else if (/Windows/.test(ua)) {
    device = 'Windows';
  } else if (/Mac/.test(ua)) {
    device = 'Mac';
  } else if (/Linux/.test(ua)) {
    device = 'Linux';
  }
  
  const browser = /Chrome/.test(ua) ? 'Chrome' :
                  /Firefox/.test(ua) ? 'Firefox' :
                  /Safari/.test(ua) ? 'Safari' :
                  /Edge/.test(ua) ? 'Edge' : 'Unknown';
  
  return `${browser}/${device}`;
}

/**
 * Test notification (for debugging)
 */
export const sendTestNotification = () => {
  if (!hasNotificationPermission()) {
    console.log('No notification permission');
    return;
  }

  new Notification('Test Notification', {
    body: 'This is a test notification from ScoreLink!',
    icon: '/logo192.png',
    tag: 'test'
  });
};
